/*
  Grandma Ann's — 220–240V voltage confirmation (Layer 1, theme only).
  Markup: snippets/ga-voltage-gate.liquid.

  While the product form's selected variant (input[name="id"]) is a 220V
  variant that has not been confirmed in this page view:
    - Add to Cart carries aria-disabled="true" (product-form.js then refuses
      to submit) and reads "Confirm voltage to continue"; clicking it opens
      the dialog instead of adding.
    - product-form gets .ga-voltage-locked, which hides the Shop Pay /
      Buy it now container (.shopify-payment-button).
  Confirmation is kept in memory only: it clears when the variant changes
  and is never stored, so a reload or new visit asks again.

  The dialog extends Dawn's ModalDialog (global.js): Escape, backdrop click
  and the "Go Back" button (#ModalClose-…) all close it, and closing without
  confirming always selects the 110–120V variant through the normal Dawn
  variant picker.
*/
(function () {
  if (customElements.get('ga-voltage-gate')) return;
  const ModalDialogBase = customElements.get('modal-dialog');
  if (!ModalDialogBase) return;

  class GaVoltageGate extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = document.getElementById(this.dataset.formId);
      this.productForm = this.form && this.form.closest('product-form');
      this.dialog = this.dialog || this.querySelector('ga-voltage-dialog');
      if (!this.productForm || !this.form || !this.dialog) return;

      this.idInput = this.form.querySelector('input[name="id"]');
      this.button = this.form.querySelector('[name="add"]');
      this.paymentButton = this.form.querySelector('.shopify-payment-button');
      this.gatedIds = this.dataset.gatedIds.split(',').filter(Boolean);
      this.confirmedId = null;
      this.lockedText = this.dataset.lockedText;
      this.dialog.gaGate = this;

      this.form.addEventListener('change', (event) => {
        if (event.target === this.idInput) this.onVariantChange(true);
      });
      // Capture phase on the custom element so the locked click never reaches
      // the button's own listeners or the form submit.
      this.productForm.addEventListener('click', (event) => {
        if (!this.isLocked() || !event.target.closest('[name="add"]')) return;
        event.preventDefault();
        event.stopPropagation();
        this.openDialog(this.button);
      }, true);
      this.form.addEventListener('submit', (event) => {
        if (!this.isLocked()) return;
        event.preventDefault();
        this.openDialog(this.button);
      }, true);

      // Dawn re-renders the button text and disabled state after every
      // variant change (renderProductInfo); re-apply the lock when it does.
      if (this.button) {
        this.observer = new MutationObserver(() => this.applyState());
        this.observer.observe(this.button, { attributes: true, attributeFilter: ['aria-disabled'], childList: true, subtree: true, characterData: true });
      }

      // A 220V variant preselected by URL is locked, but the dialog only
      // opens when the customer tries to add it.
      this.onVariantChange(false);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.dialog && this.dialog.isConnected && !this.contains(this.dialog)) this.dialog.remove();
    }

    currentId() {
      return this.idInput ? String(this.idInput.value) : '';
    }

    isGated() {
      return this.gatedIds.includes(this.currentId());
    }

    isLocked() {
      return this.isGated() && this.confirmedId !== this.currentId();
    }

    onVariantChange(fromUser) {
      if (this.confirmedId !== this.currentId()) this.confirmedId = null;
      this.applyState();
      if (fromUser && this.isLocked() && !this.dialog.hasAttribute('open')) {
        this.openDialog(this.findPickerInput() || this.button);
      }
    }

    textSpan() {
      return this.button && this.button.querySelector(':scope > span');
    }

    applyState() {
      if (this.applying) return;
      this.applying = true;
      const locked = this.isLocked();
      const span = this.textSpan();

      this.productForm.classList.toggle('ga-voltage-locked', locked);
      if (this.paymentButton) this.paymentButton.toggleAttribute('inert', locked);

      if (this.button && span) {
        if (locked) {
          if (span.textContent.trim() !== this.lockedText) {
            this.unlockedText = span.textContent;
            span.textContent = this.lockedText;
          }
          if (this.button.getAttribute('aria-disabled') !== 'true') this.button.setAttribute('aria-disabled', 'true');
          this.button.setAttribute('aria-describedby', this.dataset.helpId);
          this.button.setAttribute('aria-haspopup', 'dialog');
        } else if (this.button.hasAttribute('aria-describedby')) {
          // Leaving the locked state: restore Dawn's own label and state.
          if (span.textContent.trim() === this.lockedText && this.unlockedText) span.textContent = this.unlockedText;
          if (!this.button.classList.contains('loading')) this.button.removeAttribute('aria-disabled');
          this.button.removeAttribute('aria-describedby');
          if (!document.querySelector('cart-drawer')) this.button.removeAttribute('aria-haspopup');
        }
      }
      this.observer && this.observer.takeRecords();
      this.applying = false;
    }

    findPickerInput() {
      const formId = CSS.escape(this.dataset.formId);
      return document.querySelector(`input[type="radio"][form="${formId}"]:checked, select[form="${formId}"]`);
    }

    openDialog(opener) {
      this.dialog.show(opener);
    }

    confirm() {
      this.confirmedId = this.currentId();
      this.applyState();
    }

    goBack() {
      const safeId = this.dataset.safeId;
      const safeOptions = JSON.parse(this.dataset.safeOptions || '[]');
      const formId = CSS.escape(this.dataset.formId);
      const radios = document.querySelectorAll(`input[type="radio"][form="${formId}"]`);
      const selects = document.querySelectorAll(`select[form="${formId}"]`);
      let changedEl = null;

      if (radios.length) {
        const fieldsets = new Set(Array.from(radios, (r) => r.closest('fieldset')));
        Array.from(fieldsets).forEach((fieldset, index) => {
          const target = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((r) => r.value === safeOptions[index]);
          if (target && !target.checked) {
            target.checked = true;
            changedEl = target;
          }
        });
      } else if (selects.length) {
        selects.forEach((select, index) => {
          if (safeOptions[index] !== undefined && select.value !== safeOptions[index]) {
            select.value = safeOptions[index];
            changedEl = select;
          }
        });
      }

      // Let Dawn's variant picker update price, URL and the form's id input.
      if (changedEl) changedEl.dispatchEvent(new Event('change', { bubbles: true }));

      // No picker in this section (e.g. a featured-product CTA), or the
      // picker could not update the form: set the variant id directly.
      if (this.currentId() !== String(safeId) && this.idInput) {
        this.idInput.value = safeId;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.confirmedId = null;
      this.applyState();
      return this.findPickerInput() || this.button;
    }
  }

  class GaVoltageDialog extends ModalDialogBase {
    constructor() {
      super();
      this.querySelector('[data-ga-voltage-confirm]').addEventListener('click', () => {
        this.confirming = true;
        this.hide();
      });
    }

    connectedCallback() {
      // Link to the gate before ModalDialog moves this element to <body>.
      if (!this.gaGate) {
        const gate = this.closest('ga-voltage-gate');
        if (gate) {
          this.gaGate = gate;
          if (!gate.dialog) gate.dialog = this;
        }
      }
      super.connectedCallback();
    }

    show(opener) {
      // Third-party widgets (chat, reviews) are appended to <body> late with
      // the maximum z-index; be the last child so the dialog stays on top.
      if (this.parentElement === document.body && document.body.lastElementChild !== this) document.body.appendChild(this);
      super.show(opener);
      trapFocus(this, this.querySelector('.ga-voltage-modal__back'));
    }

    hide() {
      if (!this.hasAttribute('open')) return;
      const gate = this.gaGate;
      const confirming = this.confirming;
      this.confirming = false;

      if (gate) {
        if (confirming) {
          gate.confirm();
          if (this.openedBy && this.openedBy.getAttribute('name') !== 'add' && gate.button) this.openedBy = gate.button;
        } else {
          this.openedBy = gate.goBack();
        }
      }
      super.hide();

      // Opened from inside another Dawn modal (collection quick add):
      // keep that modal's scroll lock and focus trap.
      const outer = document.querySelector('quick-add-modal[open]');
      if (outer) {
        document.body.classList.add('overflow-hidden');
        trapFocus(outer, this.openedBy || outer.querySelector('[role="dialog"]'));
      }
    }
  }

  // Gate first: it must grab its dialog before ModalDialog moves it to <body>.
  customElements.define('ga-voltage-gate', GaVoltageGate);
  customElements.define('ga-voltage-dialog', GaVoltageDialog);
})();
